import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { FiData } from "../types/build";
import { fetchFiData } from "../services/api";
import { deleteBuildById } from "../services/api";
import { deleteAllBuildsByConfig as deleteAllBuildsAPI } from "../services/api";
import { unmarkBuildById } from "../services/api";
import { downloadAppFile, downloadAppBundleFile, downloadBuildArchiveFile } from "../services/api";
// import { useAuthStore } from '../stores/authStore';
import { fetchUserActions } from "../services/api";
import { logger } from "../utils/logger";
import { getAPIErrorMessage } from "../types/api";

export const useFiStore = defineStore("fiStore", () => {
  const fiData = ref<FiData | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const fiNumber = ref<string | null>(null);
  const actions = ref([]);
  const getFiNumber = computed(() => fiNumber.value);
  // const configId = ref<string | null>(null)   // ✅ Added
  // const buildId = ref<string | null>(null)    // ✅ Added
  const configId = ref("");
  const buildId = ref("");

  function setConfigId(val: string) {
    configId.value = val;
  }

  function setBuildId(val: string) {
    buildId.value = val;
  }

  function getConfigId() {
    return configId.value;
  }

  function getBuildId() {
    return buildId.value;
  }

  async function loadFiDataFromAPI(numberOverride?: string) {
    loading.value = true;
    error.value = null;

    try {
      const numberToUse = numberOverride || fiNumber.value;
      if (!numberToUse) throw new Error("No FI number available for API call");

      const data = await fetchFiData(numberToUse); // make sure this accepts fiNumber
      fiData.value = data.fiData || null;
      logger.debug("✅ Loaded FI data for:", numberToUse);
    } catch (err: unknown) {
      const message = getAPIErrorMessage(err, "Failed to load FI data.");
      logger.error("Failed to load FI data:", message);
      error.value = message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  const getFiData = computed(() => fiData.value);
  // const getFiNumber = computed(() => fiNumber.value);

  function setFiNumber(value: string) {
    fiNumber.value = value;
  }

  async function downloadApp(buildId: string) {
    try {
      logger.debug("Downloading App ZIP:", buildId);
      await downloadAppFile(buildId);
    } catch (err) {
      error.value = getAPIErrorMessage(err, "Failed to download app.");
      throw err;
    }
  }

  async function downloadAppBundle(buildId: string) {
    try {
      logger.debug("Downloading App Bundle:", buildId);
      await downloadAppBundleFile(buildId);
    } catch (err) {
      error.value = getAPIErrorMessage(err, "Failed to download app bundle.");
      throw err;
    }
  }

  async function downloadBuildArchive(buildId: string) {
    try {
      logger.debug("Downloading Build Archive for:", buildId);
      await downloadBuildArchiveFile(buildId);
    } catch (err) {
      error.value = getAPIErrorMessage(err, "Failed to download archive.");
      throw err;
    }
  }

  async function deleteBuild(buildId: string) {
    try {
      logger.debug("Deleting build:", buildId, fiNumber.value);
      await deleteBuildById(buildId);
    } catch (err) {
      error.value = getAPIErrorMessage(err, "Failed to delete build.");
      throw err;
    }
  }

  async function deleteAllBuildsByConfig(configId: string) {
    try {
      logger.debug("Calling delete for config:", configId);
      await deleteAllBuildsAPI(configId);
    } catch (err) {
      error.value = getAPIErrorMessage(err, "Failed to delete configuration builds.");
      throw err;
    }
  }

  async function undeleteBuild(buildId: string) {
    try {
      logger.debug("Unmarking build:", buildId);
      const res = await unmarkBuildById(buildId);
      logger.debug("Unmark response:", res);
    } catch (err) {
      error.value = getAPIErrorMessage(err, "Failed to restore build.");
      throw err;
    }
  }

  async function loadUserActions() {
    loading.value = true;
    error.value = null;

    if (!fiNumber.value) {
      loading.value = false;
      return;
    }

    try {
      const data = await fetchUserActions(fiNumber.value);
      actions.value = data;
      logger.debug("Loaded user actions:", data);
    } catch (err) {
      error.value = getAPIErrorMessage(err, "Failed to load user actions.");
    } finally {
      loading.value = false;
    }
  }

  return {
    fiData,
    getFiData,
    loading,
    error,
    loadFiDataFromAPI,
    downloadApp,
    downloadAppBundle,
    downloadBuildArchive,
    deleteBuild,
    deleteAllBuildsByConfig,
    undeleteBuild,
    setFiNumber,
    loadUserActions,
    // configId,
    // buildId,
    setConfigId,
    setBuildId,
    getConfigId,
    getBuildId,
    actions,
    getFiNumber,
  };
});
